import React, { useEffect, useState, useCallback } from "react";
import { CircleStackIcon } from "@heroicons/react/24/outline";
import { useSelector } from "react-redux";
import CreateSubmittalModal from "../components/pipingSchedule/Pipes/CreatePipingScheduleModal";
import toast from "react-hot-toast";
import { apiClient } from "../services/api/apiClient";
import SpecTempSelectModal from "../components/pipingSchedule/Pipes/SpecTempSelectModal";
import SpecificationFormModal from "../components/pipingSchedule/Pipes/SpecificationFormModal";
import PipesTable from "../components/pipingSchedule/Pipes/PipesTable";
import ValvesTable from "../components/pipingSchedule/Valves/ValvesTable";
import HangersMainTab from "../components/pipingSchedule/Hangers/HangersMainTab";
import GeneratePDFModal from "../components/pipingSchedule/Pipes/GeneratePDFModal";
import AddHangerModal from "../components/pipingSchedule/Hangers/AddHangerModal";
import AddSpacingModal from "../components/pipingSchedule/Hangers/AddSpacingModal";
import AddRiserClampModal from "../components/pipingSchedule/Hangers/AddRiserClampModal";
import AddUnistrutModal from "../components/pipingSchedule/Hangers/AddUnistrutModal";
import ImportHangerGroupModal from "../components/pipingSchedule/Hangers/ImportHangerGroupModal";
import { appInsights } from "../services/analytics/appInsights";
import { useMsal } from "@azure/msal-react";

function PipingSchedule() {
  // Get the selected project id from the redux store
  const selectedProjectId = useSelector(
    (state) => state.project.selectedProjectId
  );
  const [submittal, setSubmittal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [isAddSpecModalOpen, setIsAddSpecModalOpen] = useState(false);
  const [isDetailedFormOpen, setIsDetailedFormOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  // the refresh key is used to refresh the size definitions table when a new specification is added it forces useEffect to run again and refetch the size definitions from the backend
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState("Pipes");

  // State for size definitions
  const [sizeDefinitions, setSizeDefinitions] = useState([]);
  const [loadingSizeDefinitions, setLoadingSizeDefinitions] = useState(false);
  const [errorSizeDefinitions, setErrorSizeDefinitions] = useState(null);
  const [newSubmittalFlowActive, setNewSubmittalFlowActive] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isContinuousAddingMode, setIsContinuousAddingMode] = useState(false);

  // State for hanger modal
  const [isAddHangerModalOpen, setIsAddHangerModalOpen] = useState(false);
  const [editHangerData, setEditHangerData] = useState(null);
  const [isAddSpacingModalOpen, setIsAddSpacingModalOpen] = useState(false);
  const [editSpacingData, setEditSpacingData] = useState(null);
  const [isAddRiserClampModalOpen, setIsAddRiserClampModalOpen] =
    useState(false);
  const [editRiserClampData, setEditRiserClampData] = useState(null);
  const [isAddUnistrutModalOpen, setIsAddUnistrutModalOpen] = useState(false);
  const [editUnistrutData, setEditUnistrutData] = useState(null);
  const [isImportHangerGroupModalOpen, setIsImportHangerGroupModalOpen] =
    useState(false);
  const [currentHangerSubTab, setCurrentHangerSubTab] = useState("hangers");
  const [currentTrade, setCurrentTrade] = useState("HVAC");

  // Get the user name from the MSAL instance
  const { accounts } = useMsal();
  const userName = accounts && accounts[0] ? accounts[0].name : "Unknown";
  const [projectName, setProjectName] = useState("");

  // Fetch project name when selectedProjectId changes this is only for app insights tracking
  useEffect(() => {
    const fetchProjectName = async () => {
      if (!selectedProjectId) {
        setProjectName("");
        return;
      }
      try {
        const response = await apiClient.get(`/projects/${selectedProjectId}`);
        // Extract data from the new standardized response format
        const projectData = response.data;
        setProjectName(projectData.job_name || "");
      } catch (error) {
        setProjectName("");
      }
    };
    fetchProjectName();
  }, [selectedProjectId]);

  const openAddSpecModal = useCallback(() => {
    setEditData(null);
    setIsAddSpecModalOpen(true);
  }, []);

  // Effect for when selectedProjectId changes to get the submittal data for the piping schedule this just gets the main piping submittal data
  useEffect(() => {
    // Clear previous data when project changes
    setSubmittal(null);
    setSizeDefinitions([]);
    setError(null);
    setErrorSizeDefinitions(null);
    setLoading(true); // Set loading true for the submittal fetch
    setNewSubmittalFlowActive(false); // Reset flow on project change
    setActiveTab("Pipes"); // Always reset to Pipes tab when project changes

    if (!selectedProjectId) {
      setError("No project selected");
      setLoading(false);
      return;
    }

    // this fetches the piping schedule data from the database
    const fetchSubmittalData = async () => {
      try {
        const response = await apiClient.get(
          `/piping-schedule/${selectedProjectId}`
        );
        // Extract data from the new standardized response format
        const pipingScheduleData = response.data;
        console.log("PipingSchedule: pipingScheduleData", pipingScheduleData);
        setSubmittal(pipingScheduleData);
        setError(null); // Clear any previous "Submittal not found" error
      } catch (err) {
        console.error("PipingSchedule: Failed to fetch submittal:", err);
        // If submittal not found, it's not necessarily an error for the initial view
        if (err.message.includes("404") || err.message.includes("not found")) {
          setSubmittal(null); // Explicitly set to null
          setError("Submittal not found"); // Specific error for UI to handle create flow
        } else {
          setError(err.message);
          setSubmittal(null);
          toast.error(`Failed to fetch submittal: ${err.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSubmittalData();
  }, [selectedProjectId]);

  // Effect to fetch size definitions for the submittal when submittal.id is available or refreshKey changes sorts the data by spec name
  useEffect(() => {
    const fetchSizeDefinitions = async () => {
      if (!submittal || !submittal.id) {
        setSizeDefinitions([]);
        return;
      }

      setLoadingSizeDefinitions(true);
      setErrorSizeDefinitions(null);
      try {
        const response = await apiClient.get(
          `/size_definitions/submittal/${submittal.id}`
        );
        // Extract data from the new standardized response format
        const data = response.data;

        console.log("PipingSchedule: sizeDefinitions", data);
        // Sort data by spec_name: numerical prefix then alphabetical
        data.sort((a, b) => {
          const regex = /^(\d*\.?\d*)\s*(.*)$/; // Extracts numeric prefix and the rest

          const matchA = String(a.spec_name || "").match(regex);
          const matchB = String(b.spec_name || "").match(regex);

          const numA = matchA && matchA[1] ? parseFloat(matchA[1]) : Infinity;
          const strA =
            matchA && matchA[2]
              ? matchA[2].toLowerCase()
              : String(a.spec_name || "").toLowerCase();

          const numB = matchB && matchB[1] ? parseFloat(matchB[1]) : Infinity;
          const strB =
            matchB && matchB[2]
              ? matchB[2].toLowerCase()
              : String(b.spec_name || "").toLowerCase();

          if (numA !== numB) {
            return numA - numB;
          }
          return strA.localeCompare(strB);
        });

        setSizeDefinitions(data);
      } catch (err) {
        console.error("PipingSchedule: Failed to fetch size definitions:", err);
        setErrorSizeDefinitions(err.message);
        setSizeDefinitions([]);
        toast.error(`Failed to load size definitions: ${err.message}`);
      } finally {
        setLoadingSizeDefinitions(false);
      }
    };

    if (submittal && submittal.id) {
      fetchSizeDefinitions();
    } else {
      setSizeDefinitions([]);
      if (newSubmittalFlowActive) {
        setNewSubmittalFlowActive(false);
      }
    }
  }, [submittal, refreshKey, newSubmittalFlowActive]);

  // New Effect to handle auto-opening modal
  useEffect(() => {
    if (
      newSubmittalFlowActive &&
      !loadingSizeDefinitions &&
      submittal &&
      submittal.id &&
      sizeDefinitions.length === 0
    ) {
      openAddSpecModal();
      setNewSubmittalFlowActive(false); // Reset flow
    }
  }, [
    newSubmittalFlowActive,
    loadingSizeDefinitions,
    sizeDefinitions,
    submittal,
    openAddSpecModal,
  ]);

  // Modal handlers from old Home.js
  const closeAddSpecModal = () => {
    setIsAddSpecModalOpen(false);
    setIsContinuousAddingMode(false);
  };

  const openDetailedForm = () => {
    setIsDetailedFormOpen(true);
  };

  const closeDetailedForm = () => {
    setIsDetailedFormOpen(false);
    setEditData(null);
  };

  const handleFormSubmit = (formData) => {
    closeDetailedForm();
    setRefreshKey((prev) => prev + 1);
  };

  const handleSubmitSpecification = (templateOrNullData) => {
    // Always close the SpecificationModal and process the data
    closeAddSpecModal();

    if (templateOrNullData) {
      // Data came from a template
      setEditData(templateOrNullData);
    } else {
      // No template selected, creating a blank specification
      setEditData(null);
    }

    // Always open DetailedFormModal
    openDetailedForm();
    setRefreshKey((prev) => prev + 1);
  };

  const handleEdit = (data) => {
    setEditData(data);
    openDetailedForm();
  };

  const handleNewSpecification = () => {
    closeDetailedForm();
    setEditData(null);
    setIsContinuousAddingMode(true);
    openAddSpecModal();
  };

  const handleDelete = async (specId) => {
    setRefreshKey((prev) => prev + 1);
  };

  const openPdfModal = () => {
    setIsPdfModalOpen(true);
    setIsGeneratingPdf(true);
  };
  const closePdfModal = () => {
    setIsPdfModalOpen(false);
    setIsGeneratingPdf(false);
  };

  // I am commenting this out I dont belive it is used anymore jsp
  // const generatePDF = async (submittalId) => {
  //   setIsGeneratingPdf(true);
  //   const toastId = toast.loading("Generating Submittal...");
  //   try {
  //     await apiClient.get(`/pdf/${submittalId}`);
  //     toast.success("Submittal generated successfully!", { id: toastId });
  //   } catch (error) {
  //     console.error("PipingSchedule: Failed to generate submittal:", error);
  //     toast.error(`Failed to generate submittal: ${error.message}`, { id: toastId });
  //   } finally {
  //     setIsGeneratingPdf(false);
  //   }
  // };

  const handleCreateSubmittal = async (data) => {
    if (!selectedProjectId) {
      toast.error("No project selected. Cannot create submittal.");
      return;
    }

    const toastId = toast.loading("Creating submittal...");
    try {
      const response = await apiClient.post(
        `/piping-schedule/${selectedProjectId}`,
        data
      );
      // Extract data from the new standardized response format
      const newSubmittalData = response.data;

      // Track the creation of a new submittal
      appInsights.trackEvent({
        name: "NewSubmittalCreated",
        properties: {
          user: userName,
          projectId: selectedProjectId,
          projectName: projectName,
          propress: data.propress,
          grooved: data.grooved,
          pvc: data.pvc,
        },
      });
      toast.success("Submittal created successfully!", { id: toastId });
      setIsCreateModalOpen(false);
      setSubmittal(newSubmittalData);
      setError(null);
      setLoading(false);
      setNewSubmittalFlowActive(true);
    } catch (error) {
      toast.error(`Failed to create submittal: ${error.message}`, {
        id: toastId,
      });
      setLoading(false);
    }
  };

  const handleAddButtonClick = () => {
    if (activeTab === "Hangers") {
      // Clear edit data when adding new
      setEditHangerData(null);
      setEditSpacingData(null);
      setEditRiserClampData(null);
      setEditUnistrutData(null);
      // Open different modals based on current sub-tab
      switch (currentHangerSubTab) {
        case "hangers":
          setIsAddHangerModalOpen(true);
          break;
        case "spacing":
          setIsAddSpacingModalOpen(true);
          break;
        case "riser-clamps":
          setIsAddRiserClampModalOpen(true);
          break;
        case "unistrut":
          setIsAddUnistrutModalOpen(true);
          break;
        default:
          setIsAddHangerModalOpen(true);
      }
    } else {
      openAddSpecModal();
    }
  };

  const getButtonText = () => {
    if (activeTab === "Hangers") {
      switch (currentHangerSubTab) {
        case "hangers":
          return "Add Hanger";
        case "spacing":
          return "Add Spacing";
        case "riser-clamps":
          return "Add Riser Clamp";
        case "unistrut":
          return "Add Unistrut Attachment";
        default:
          return "Add Hanger";
      }
    }
    return "Add Specification";
  };

  const handleImportHangerGroup = () => {
    setIsImportHangerGroupModalOpen(true);
  };

  const handleHangerTradeChange = (trade) => {
    setCurrentTrade(trade);
    setCurrentHangerSubTab("hangers"); // Reset sub-tab when trade changes
  };

  const handleHangerSubTabChange = (subTab) => {
    setCurrentHangerSubTab(subTab);
  };

  const handleEditHanger = (hangerData) => {
    setEditHangerData(hangerData);
    setIsAddHangerModalOpen(true);
  };

  const handleDeleteHanger = async (hangerData) => {
    console.log("Delete hanger clicked:", hangerData);

    // Show confirmation dialog
    const isConfirmed = window.confirm(
      `Are you sure you want to delete this hanger?\n\nMaterial: ${
        hangerData.material || "N/A"
      }\nHanger: ${
        hangerData.hanger_product || "N/A"
      }\n\nThis action cannot be undone.`
    );

    if (!isConfirmed) {
      return;
    }

    try {
      const response = await apiClient.delete(`/hangers/${hangerData.id}`);

      // Check if the deletion was successful (the new API returns standardized responses)
      if (!response.success) {
        throw new Error(response.error || "Failed to delete hanger");
      }

      console.log("Hanger deleted successfully:", response);
      toast.success("Hanger deleted successfully!");
      // Refresh the table
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error deleting hanger:", error);
      toast.error("Error deleting hanger. Please try again.");
    }
  };

  const handleEditSpacing = (spacingData) => {
    console.log("Edit spacing clicked:", spacingData);
    setEditSpacingData(spacingData);
    setIsAddSpacingModalOpen(true);
  };

  const handleDeleteSpacing = async (spacingData) => {
    console.log("Delete spacing clicked:", spacingData);

    // Show confirmation dialog
    const isConfirmed = window.confirm(
      `Are you sure you want to delete this spacing?\n\nMaterial: ${
        spacingData.material || "N/A"
      }\nSpacing: ${
        spacingData.spacing || "N/A"
      }\n\nThis action cannot be undone.`
    );

    if (!isConfirmed) {
      return;
    }

    try {
      const response = await apiClient.delete(
        `/hangers/spacing/${spacingData.id}`
      );

      // Check if the deletion was successful (the new API returns standardized responses)
      if (!response.success) {
        throw new Error(response.error || "Failed to delete spacing");
      }

      console.log("Spacing deleted successfully:", response);
      toast.success("Spacing deleted successfully!");
      // Refresh the table
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error deleting spacing:", error);
      toast.error("Error deleting spacing. Please try again.");
    }
  };

  const handleEditUnistrut = (unistrutData) => {
    console.log("Edit unistrut clicked:", unistrutData);
    setEditUnistrutData(unistrutData);
    setIsAddUnistrutModalOpen(true);
  };

  const handleDeleteUnistrut = async (unistrutData) => {
    console.log("Delete unistrut clicked:", unistrutData);

    // Show confirmation dialog
    const isConfirmed = window.confirm(
      `Are you sure you want to delete this unistrut?\n\nMaterial: ${
        unistrutData.material || "N/A"
      }\nUnistrut Product: ${
        unistrutData.unistrut_product || "N/A"
      }\n\nThis action cannot be undone.`
    );

    if (!isConfirmed) {
      return;
    }

    try {
      const response = await apiClient.delete(
        `/hangers/unistrut/${unistrutData.id}`
      );

      // Check if the deletion was successful (the new API returns standardized responses)
      if (!response.success) {
        throw new Error(response.error || "Failed to delete unistrut");
      }

      console.log("Unistrut deleted successfully:", response);
      toast.success("Unistrut deleted successfully!");
      // Refresh the table
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error deleting unistrut:", error);
      toast.error("Error deleting unistrut. Please try again.");
    }
  };

  const handleEditRiserClamp = (riserClampData) => {
    console.log("Edit riser clamp clicked:", riserClampData);
    setEditRiserClampData(riserClampData);
    setIsAddRiserClampModalOpen(true);
  };

  const handleDeleteRiserClamp = async (riserClampData) => {
    console.log("Delete riser clamp clicked:", riserClampData);

    // Show confirmation dialog
    const isConfirmed = window.confirm(
      `Are you sure you want to delete this riser clamp?\n\nMaterial: ${
        riserClampData.material || "N/A"
      }\nRiser Clamp: ${
        riserClampData.riser_clamp_product || "N/A"
      }\n\nThis action cannot be undone.`
    );

    if (!isConfirmed) {
      return;
    }

    try {
      const response = await apiClient.delete(
        `/hangers/riser-clamps/${riserClampData.id}`
      );

      // Check if the deletion was successful (the new API returns standardized responses)
      if (!response.success) {
        throw new Error(response.error || "Failed to delete riser clamp");
      }

      console.log("Riser clamp deleted successfully:", response);
      toast.success("Riser clamp deleted successfully!");
      // Refresh the table
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error deleting riser clamp:", error);
      toast.error("Error deleting riser clamp. Please try again.");
    }
  };

  const renderActiveTab = () => {
    // if (loadingSizeDefinitions) {
    //   return <p>Loading size definitions...</p>;
    // }
    // if (errorSizeDefinitions) {
    //   return <p>Error loading size definitions: {errorSizeDefinitions}</p>;
    // }
    if (!submittal || !submittal.id) {
      return <p>No submittal selected or found to display data.</p>;
    }
    if (["Pipes", "Valves"].includes(activeTab)) {
      if (loadingSizeDefinitions) {
        return <p>Loading size definitions...</p>;
      }
      if (errorSizeDefinitions) {
        return <p>Error loading size definitions: {errorSizeDefinitions}</p>;
      }
      if (sizeDefinitions.length === 0 && !loadingSizeDefinitions) {
        return <p>No size definitions found for this submittal.</p>;
      }
    }

    switch (activeTab) {
      case "Pipes":
        return (
          <PipesTable
            key={refreshKey}
            onEdit={handleEdit}
            onDelete={handleDelete}
            submittalData={sizeDefinitions}
          />
        );
      case "Valves":
        return (
          <ValvesTable
            key={refreshKey}
            onEdit={handleEdit}
            onDelete={handleDelete}
            submittalData={sizeDefinitions}
          />
        );
      case "Hangers":
        return (
          <HangersMainTab
            key={refreshKey}
            submittalId={submittal.id}
            activeTrade={currentTrade}
            activeSubTab={currentHangerSubTab}
            onTradeChange={handleHangerTradeChange}
            onSubTabChange={handleHangerSubTabChange}
            onEditHanger={handleEditHanger}
            onDeleteHanger={handleDeleteHanger}
            onEditSpacing={handleEditSpacing}
            onDeleteSpacing={handleDeleteSpacing}
            onEditRiserClamp={handleEditRiserClamp}
            onDeleteRiserClamp={handleDeleteRiserClamp}
            onEditUnistrut={handleEditUnistrut}
            onDeleteUnistrut={handleDeleteUnistrut}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center min-h-screen bg-gray-100 p-4 overflow-y-auto">
        <p>Loading project submittal data...</p>
      </div>
    );
  }

  // This error handles cases where projectId is not selected or a general fetch error occurs
  if (error && error !== "Submittal not found") {
    return (
      <div className="flex flex-1 items-center justify-center min-h-screen bg-gray-100 p-4 overflow-y-auto">
        {error === "No project selected" ? (
          <p>Please select a project to view submittals.</p>
        ) : (
          <p>Error fetching submittal: {error}</p>
        )}
      </div>
    );
  }

  if (
    !loading &&
    (!submittal || submittal.message === "Submittal not found") &&
    selectedProjectId
  ) {
    return (
      <div className="flex flex-1 items-center justify-center min-h-screen bg-gray-100 p-4 overflow-y-auto">
        <div
          onClick={() => setIsCreateModalOpen(true)}
          className="flex flex-1 items-center justify-center min-h-screen bg-gray-100"
        >
          <div className="border-2 border-dashed border-gray-300 rounded-lg px-12 py-8 min-w-[300px] flex flex-col items-center justify-center cursor-pointer hover:shadow-md transition">
            <CircleStackIcon className="w-10 h-10 mb-2 text-gray-600" />
            <p className="text-md text-gray-600">
              Create a new piping schedule
            </p>
          </div>
        </div>
        <CreateSubmittalModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handleCreateSubmittal}
          projectId={selectedProjectId}
        />
      </div>
    );
  }

  // this is the main UI for the piping schedule and shows the tabs for the pipes, valves, and hangers and then based on the clicked tab it will show the table for that tab
  // If we have a valid submittal with an ID, show the main UI
  if (submittal && submittal.id) {
    return (
      <div className="p-4 md:p-8 bg-gray-100 min-h-screen flex-1 overflow-y-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 ">
          <div className="flex justify-center space-x-2 md:space-x-4 mb-4 md:mb-0 overflow-x-auto">
            <button
              onClick={() => setActiveTab("Pipes")}
              className={`px-3 py-2 md:px-4 rounded-t-lg font-semibold text-sm md:text-base ${
                activeTab === "Pipes"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-600"
              }`}
            >
              Pipes
            </button>
            <button
              onClick={() => setActiveTab("Valves")}
              className={`px-3 py-2 md:px-4 rounded-t-lg font-semibold text-sm md:text-base ${
                activeTab === "Valves"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-600"
              }`}
            >
              Valves
            </button>
            <button
              onClick={() => setActiveTab("Hangers")}
              className={`px-3 py-2 md:px-4 rounded-t-lg font-semibold text-sm md:text-base ${
                activeTab === "Hangers"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-600"
              }`}
            >
              Hangers
            </button>
          </div>
          {/* this is the div for the add specification and create submittal buttons */}
          <div className="flex space-x-2 md:space-x-4">
            {activeTab === "Hangers" && (
              <div className="text-xs text-gray-600 self-center mr-2">
                {/* Current: {currentTrade} - {currentHangerSubTab} */}
              </div>
            )}
            <button
              onClick={handleAddButtonClick}
              className="bg-white text-gray-800 py-2 px-3 md:px-4 rounded-lg font-bold border border-gray-300 shadow-md text-sm md:text-base"
            >
              {getButtonText()}
            </button>
            {activeTab === "Hangers" && (
              <button
                onClick={handleImportHangerGroup}
                className="bg-green-600 text-white py-2 px-3 md:px-4 rounded-lg font-bold border border-gray-300 shadow-md text-sm md:text-base"
              >
                Import Hanger Specs
              </button>
            )}
            <button
              onClick={openPdfModal}
              className="bg-indigo-600 text-white py-2 px-3 md:px-4 rounded-lg font-bold border border-gray-300 shadow-md text-sm md:text-base"
              disabled={isGeneratingPdf}
            >
              {isGeneratingPdf ? "Generating..." : "Create Submittal"}
            </button>
          </div>
        </div>

        <SpecTempSelectModal
          isOpen={isAddSpecModalOpen}
          onClose={closeAddSpecModal}
          title={
            isContinuousAddingMode
              ? "Create the next specification"
              : "Create a new specification"
          }
          onSubmit={handleSubmitSpecification}
          pvfSubmittalId={submittal.id}
          propress={submittal?.propress}
          grooved={submittal?.grooved}
          pvc={submittal?.pvc}
          onNewSpecification={handleNewSpecification}
          isContinuousAddingMode={isContinuousAddingMode}
        />
        <SpecificationFormModal
          isOpen={isDetailedFormOpen}
          onClose={closeDetailedForm}
          onSubmit={handleFormSubmit}
          editData={editData}
          pvfSubmittalId={submittal.id}
          specId={editData ? editData.id : null}
          onNewSpecification={handleNewSpecification}
          isContinuousAddingMode={isContinuousAddingMode}
        />
        {/* this is what shows the table for the selected tab the PipesTable, ValvesTable, and HangersMainTab components */}
        {renderActiveTab()}
        {isPdfModalOpen && (
          <GeneratePDFModal
            onClose={closePdfModal}
            submittalId={submittal?.id}
          />
        )}
        {isAddHangerModalOpen && (
          <AddHangerModal
            isOpen={isAddHangerModalOpen}
            onClose={() => {
              setIsAddHangerModalOpen(false);
              setEditHangerData(null);
            }}
            trade={currentTrade}
            submittalId={submittal.id}
            editData={editHangerData}
            onSuccess={() => {
              setRefreshKey((prev) => prev + 1);
            }}
          />
        )}
        {isAddSpacingModalOpen && (
          <AddSpacingModal
            isOpen={isAddSpacingModalOpen}
            onClose={() => {
              setIsAddSpacingModalOpen(false);
              setEditSpacingData(null);
              setEditUnistrutData(null);
            }}
            trade={currentTrade}
            submittalId={submittal.id}
            editData={editSpacingData}
            onSuccess={() => {
              setRefreshKey((prev) => prev + 1);
            }}
          />
        )}
        {isAddRiserClampModalOpen && (
          <AddRiserClampModal
            isOpen={isAddRiserClampModalOpen}
            onClose={() => {
              setIsAddRiserClampModalOpen(false);
              setEditRiserClampData(null);
            }}
            trade={currentTrade}
            submittalId={submittal.id}
            editData={editRiserClampData}
            onSuccess={() => {
              setRefreshKey((prev) => prev + 1);
            }}
          />
        )}
        {isAddUnistrutModalOpen && (
          <AddUnistrutModal
            isOpen={isAddUnistrutModalOpen}
            onClose={() => {
              setIsAddUnistrutModalOpen(false);
              setEditUnistrutData(null);
            }}
            trade={currentTrade}
            submittalId={submittal.id}
            editData={editUnistrutData}
            onSuccess={() => {
              setRefreshKey((prev) => prev + 1);
            }}
          />
        )}
        {isImportHangerGroupModalOpen && (
          <ImportHangerGroupModal
            isOpen={isImportHangerGroupModalOpen}
            onClose={() => {
              setIsImportHangerGroupModalOpen(false);
            }}
            trade={currentTrade}
            submittalId={submittal.id}
            onSuccess={() => {
              setRefreshKey((prev) => prev + 1);
            }}
          />
        )}
      </div>
    );
  }

  // Fallback for any other state (transitional or unexpected)
  return (
    <div className="flex flex-1 items-center justify-center min-h-screen bg-gray-100 p-4 overflow-y-auto">
      <p>Preparing project data...</p>
    </div>
  );
}

export default PipingSchedule;
